import { useState, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import YupPassword from "yup-password";
import dayjs from "dayjs";

import axios from "axios";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Container,
  Card,
  Typography,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
  CircularProgress,
  IconButton,
  InputAdornment,
  CardHeader,
  CardContent,
  Switch,
  FormControlLabel,
  Alert,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { LoadingButton } from "@mui/lab";
import { fetchNotaryData } from "../../store/actions/notaryActions";
import ChangeFA from "../ChangeFA";
import ChangeNotaryAvailability from "./dashboardComponents/ChangeNotaryAvailability";
import { enableNotifications, disableNotifications } from "../../utils/push";
import { openBlankTab, setNewTabLocation } from "../../utils/openNewTab";
import { timezoneMappings } from "../../data/timezones";
import TimezoneSelect from "../layout/TimezoneSelect";
YupPassword(Yup);

const NotarySettings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const currentUser = useSelector((state) => state.notarySession.user);
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const notaryJobs = useSelector((state) => state.notarySession.notaryJobs);

  const [errorTextPassword, setErrorTextPassword] = useState("");
  const [errorTextNewsletter, setErrorTextNewsletter] = useState("");
  const [errorTextCancel, setErrorTextCancel] = useState("");
  const [errorTextRemove, setErrorTextRemove] = useState("");
  const [errorTextBlueNotary, setErrorTextBlueNotary] = useState("");

  const [dataLoading, setDataLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [readyChange, setReadyChange] = useState(true);
  const [changeErrorText, setChangeErrorText] = useState("");

  const [imageList, setImageList] = useState([]);
  const [currentFA, setCurrentFA] = useState("");
  const [currentImg, setCurrentImg] = useState("");
  const [imgWidth, setImgWidth] = useState("");
  const [qrCodeSrc, setQRCodeSrc] = useState(null);

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [saveLoading, setSaveLoading] = useState(false);
  const [availabilities, setAvailabilities] = useState([]);
  const [availableNonstop, setAvailableNonstop] = useState(false);
  const daysOfWeek = useMemo(
    () => [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ],
    [],
  );

  const [notifyText, setNotifyText] = useState("");
  const [timezone, setTimezone] = useState("America/New_York");

  const currentUserRef = useRef(currentUser); // Use a ref to hold the currentUser

  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedStartTime, setSelectedStartTime] = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);
  const [prefillLoading, setPrefillLoading] = useState(false);

  // Push notification states
  const [pushNotificationsEnabled, setPushNotificationsEnabled] =
    useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationError, setNotificationError] = useState("");
  const [notificationSuccess, setNotificationSuccess] = useState("");
  const [notificationPermission, setNotificationPermission] =
    useState("default");

  useEffect(() => {
    // Update the ref whenever currentUser changes
    currentUserRef.current = currentUser;
  }, [currentUser]);

  const countRef = useRef(0);

  useEffect(() => {
    if (errorTextPassword !== "") {
      const passwordTimer = setTimeout(() => setErrorTextPassword(""), 3000);
      return () => clearTimeout(passwordTimer);
    }

    if (changeErrorText !== "") {
      const changeAvTimer = setTimeout(() => setChangeErrorText(""), 5000);
      return () => clearTimeout(changeAvTimer);
    }

    if (notifyText !== "") {
      const notifyTextTimer = setTimeout(() => setNotifyText(""), 5000);
      return () => clearTimeout(notifyTextTimer);
    }

    if (errorTextNewsletter !== "") {
      const contractorTimer = setTimeout(
        () => setErrorTextNewsletter(""),
        3000,
      );
      return () => clearTimeout(contractorTimer);
    }

    if (errorTextCancel !== "") {
      const cancelTimer = setTimeout(() => setErrorTextCancel(""), 3000);
      return () => clearTimeout(cancelTimer);
    }

    if (errorTextRemove !== "") {
      const removeTimer = setTimeout(() => setErrorTextRemove(""), 3000);
      return () => clearTimeout(removeTimer);
    }

    if (notificationError !== "") {
      const notificationErrorTimer = setTimeout(
        () => setNotificationError(""),
        5000,
      );
      return () => clearTimeout(notificationErrorTimer);
    }

    if (notificationSuccess !== "") {
      const notificationSuccessTimer = setTimeout(
        () => setNotificationSuccess(""),
        5000,
      );
      return () => clearTimeout(notificationSuccessTimer);
    }

    if (errorTextBlueNotary !== "") {
      const blueNotaryTimer = setTimeout(
        () => setErrorTextBlueNotary(""),
        5000,
      );
      return () => clearTimeout(blueNotaryTimer);
    }
  }, [
    errorTextPassword,
    errorTextCancel,
    errorTextRemove,
    errorTextNewsletter,
    changeErrorText,
    notificationError,
    notificationSuccess,
    notifyText,
    errorTextBlueNotary,
  ]);

  useEffect(() => {
    const isTimezoneInList = (tz) =>
      timezoneMappings.some((timezoneOption) => timezoneOption.value === tz);

    const currentTimezone = currentUser?.timezone;
    setTimezone(
      isTimezoneInList(currentTimezone) ? currentTimezone : "America/New_York",
    );
  }, [currentUser]);

  useEffect(() => {
    const getQr = async () => {
      dispatch(fetchNotaryData(currentSession));
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/auth/2fa/generate_to_change_qr`,
          { currentSession: currentSession },
          {
            responseType: "blob",
            withCredentials: true,
          },
        );
        if (response.status === 200) {
          const qrCodeUrl = URL.createObjectURL(new Blob([response.data]));
          setQRCodeSrc(qrCodeUrl);
        }
      } catch (e) {}
    };

    // Check notification status: permission and actual subscription
    const checkNotificationState = async () => {
      if ("Notification" in window && "serviceWorker" in navigator) {
        const permission = Notification.permission;
        setNotificationPermission(permission);
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          const sub = await registration?.pushManager?.getSubscription();
          setPushNotificationsEnabled(Boolean(sub));
        } catch (e) {
          setPushNotificationsEnabled(false);
        }
      }
    };

    if (countRef.current < 1) {
      getQr();
      checkNotificationState();
      countRef.current += 1;
    }
  }, [currentSession, dispatch]);

  useEffect(() => {
    setImageList([
      {
        alt: "email",
        src: "/img/logos/emailImage.png",
        // width: 120,
        width: 80,
      },
      {
        alt: "mobile",
        src: "/img/logos/phone.png",
        // width: 180,
        width: 140,
      },

      {
        alt: "authenticator",
        src: "/img/logos/authenticator.png",
        // width: 167,
        width: 127,
      },
    ]);

    if (currentUser?.auth_type === "totp") {
      setCurrentFA("totp");
      setCurrentImg("/img/logos/authenticator.png");
      setImgWidth(127);
    } else if (currentUser?.auth_type === "emailFa") {
      setCurrentFA("emailFa");
      setCurrentImg("/img/logos/emailImage.png");
      setImgWidth(80);
    } else if (currentUser?.auth_type === "smsFa") {
      setCurrentFA("smsFa");
      setImgWidth(140);
      setCurrentImg("/img/logos/phone.png");
    }

    try {
      if (!currentUser || !notaryJobs) {
        return;
      }
      if (!currentUser?.role === "notary") {
        navigate("/login");
      }

      if (currentUser?.availabilities.length === 0) {
        navigate("/notary/finalize");
        return;
      }

      const processedAvailability = currentUser?.availabilities.map((day) => {
        return {
          ...day,
          startTime: day.startTime ? day.startTime : null,
          endTime: day.endTime ? day.endTime : null,
        };
      });
      setAvailabilities(processedAvailability);
      setAvailableNonstop(Boolean(currentUser?.available_nonstop));
    } catch (err) {}
  }, [currentUser, navigate, notaryJobs]);

  const passwordFormik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      newPasswordConfirmation: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string()
        .min(8, "password must contain 8 or more characters")
        .max(255)
        .minLowercase(1, "password must contain at least 1 lower case letter")
        .minUppercase(1, "password must contain at least 1 upper case letter")
        .minNumbers(1, "password must contain at least 1 number")
        .minSymbols(1, "password must contain at least 1 special character")
        .required("Password is required"),
      newPassword: Yup.string()
        .min(8, "password must contain 8 or more characters")
        .max(255)
        .minLowercase(1, "password must contain at least 1 lower case letter")
        .minUppercase(1, "password must contain at least 1 upper case letter")
        .minNumbers(1, "password must contain at least 1 number")
        .minSymbols(1, "password must contain at least 1 special character")
        .required("Password is required"),
      newPasswordConfirmation: Yup.string()

        .required("Password confirmation is required")
        .test("passwords-match", "Passwords must match", function (value) {
          return this.parent.newPassword === value;
        }),
    }),
    onSubmit: async (values, helpers) => {
      try {
        setLoading(true);
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/nocApi/password/changepwd`,
            {
              oldPassword: values.oldPassword,
              newPassword: values.newPassword,
              newPasswordConfirmation: values.newPasswordConfirmation,
              currentSession: currentSession,
            },
            { withCredentials: true },
          );
          if (res.data.success) {
            setErrorTextPassword(res.data.message);
            setLoading(false);
            values.oldPassword = "";
            values.newPassword = "";
            values.newPasswordConfirmation = "";
          }
        } catch (err) {
          setErrorTextPassword(err.response.data.message);
          values.oldPassword = "";
          values.newPassword = "";
          values.newPasswordConfirmation = "";
          setLoading(false);
        }
      } catch (err) {
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
        setLoading(false);
      }
    },
  });

  const daySchema = Yup.object()
    .shape({
      day: Yup.string(),
      available: Yup.boolean(),
      startTime: Yup.date()
        .nullable()
        .typeError("Start time must be a valid date and time"),
      endTime: Yup.date()
        .nullable()
        .typeError("End time must be a valid date and time"),
    })
    .test("time-conditional", "Invalid start or end time", function (obj) {
      if (obj.available) {
        const startTime = new Date(obj.startTime);
        const endTime = new Date(obj.endTime);
        const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
        const endMinutes = endTime.getHours() * 60 + endTime.getMinutes();

        if (!isValidTime(startTime)) {
          return this.createError({
            path: `${this.path}.startTime`,
            message: "Start time must be on the hour or half-hour",
          });
        }
        if (!isValidTime(endTime)) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: "End time must be on the hour or half-hour",
          });
        }

        if (!obj.startTime || isNaN(startTime.getTime())) {
          return this.createError({
            path: `${this.path}.startTime`,
            message: "Start time is required",
          });
        }
        if (!obj.endTime || isNaN(endTime.getTime())) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: "End time is required",
          });
        }

        // ⛔ BLOCK 12:00 PM as start time
        if (
          startTime.getHours() === 12 &&
          startTime.getMinutes() === 0 &&
          startTime.toLocaleTimeString("en-US", { hour12: true }).includes("PM")
        ) {
          return this.createError({
            path: `${this.path}.startTime`,
            message: "Start time cannot be 12:00 PM",
          });
        }

        // ⛔ BLOCK 12:00 PM as end time
        if (
          endTime.getHours() === 12 &&
          endTime.getMinutes() === 0 &&
          endTime.toLocaleTimeString("en-US", { hour12: true }).includes("PM")
        ) {
          return this.createError({
            path: `${this.path}.endTime`,
            message: "End time cannot be 12:00 PM",
          });
        }

        if (
          endMinutes <= startMinutes &&
          !(endTime.getHours() === 0 && endTime.getMinutes() === 0)
        ) {
          return this.createError({
            path: `${this.path}.endTime`,
            message:
              "End time must be after start time unless it's exactly 00:00 marking the end of the day.",
          });
        }

        const hourDifference =
          ((endTime - startTime + 24 * 60 * 60 * 1000) %
            (24 * 60 * 60 * 1000)) /
          (1000 * 60 * 60);

        if (hourDifference < 1) {
          return this.createError({
            path: `${this.path}.endTime`,
            message:
              "There must be at least 1 hour difference between start and end times",
          });
        }
      }
      return true;
    });

  const avaValidationSchema = Yup.object({
    availableNonstop: Yup.boolean(),
    availability: Yup.array().when(
      "availableNonstop",
      ([isAvailableNonstop], schema) => {
        if (isAvailableNonstop) {
          return schema.of(
            Yup.object().shape({
              day: Yup.string(),
              available: Yup.boolean(),
              startTime: Yup.mixed().nullable(),
              endTime: Yup.mixed().nullable(),
            }),
          );
        }

        return schema
          .of(daySchema)
          .test(
            "total-hours",
            "Minimum of 1 hours per week required",
            function (availabilityList) {
              let totalMinutes = 0;
              for (const day of availabilityList) {
                if (day.available && day.startTime && day.endTime) {
                  // Ensure startTime and endTime are Date objects
                  const startTime = new Date(day.startTime);
                  const endTime = new Date(day.endTime);
                  // Check if the dates are valid
                  if (
                    !isNaN(startTime.getTime()) &&
                    !isNaN(endTime.getTime())
                  ) {
                    const diff = endTime.getTime() - startTime.getTime();
                    totalMinutes += diff / (1000 * 60);
                  }
                }
              }
              const totalHours = totalMinutes / 60;
              return totalHours >= 1;
            },
          );
      },
    ),
  });

  const defaultInitialValues = {
    availableNonstop: false,
    availability: daysOfWeek.map((day) => ({
      day: day,
      available: false,
      startTime: null,
      endTime: null,
    })),
  };

  const avaFormik = useFormik({
    enableReinitialize: false,
    initialValues: defaultInitialValues,
    validationSchema: avaValidationSchema,
    onSubmit: async (values) => {
      const processedAvailability = values.availability.map((day) => {
        return {
          ...day,
          startTime: day.startTime ? formatTime(day.startTime) : null,
          endTime: day.endTime ? formatTime(day.endTime) : null,
        };
      });

      const payload = {
        availabilities: processedAvailability,
        available_nonstop: Boolean(values.availableNonstop),
      };

      try {
        setSaveLoading(true);
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/save-availabilities`,
          { payload, currentSession: currentSession },
          {
            withCredentials: true,
          },
        );
        if (resp.data.success) {
          setAvailabilities(processedAvailability);
          setAvailableNonstop(Boolean(values.availableNonstop));
          setReadyChange(true);
          setChangeErrorText(
            "The availabilities have been successfully saved.",
          );
        }
        setSaveLoading(false);
      } catch (e) {
        setSaveLoading(false);
      }
    },
  });

  useEffect(() => {
    if (availabilities.length === 0) return;

    const updatedValues = daysOfWeek.map((day) => {
      const availabilityData = availabilities.find((a) => a.day === day);
      return {
        day: day,
        available: availabilityData ? availabilityData.available : false,
        startTime: availabilityData
          ? createTimeFromString(availabilityData.startTime)
          : null,
        endTime: availabilityData
          ? createTimeFromString(availabilityData.endTime)
          : null,
      };
    });

    // Only update Formik if values actually changed (compare normalized HH:mm strings)
    const normalize = (list) =>
      (list || []).map((d) => ({
        day: d.day,
        available: !!d.available,
        startTime: d.startTime ? formatTime(new Date(d.startTime)) : null,
        endTime: d.endTime ? formatTime(new Date(d.endTime)) : null,
      }));

    const currentNormalized = normalize(avaFormik.values.availability);
    const nextNormalized = normalize(updatedValues);

    if (JSON.stringify(currentNormalized) !== JSON.stringify(nextNormalized)) {
      avaFormik.setValues({
        ...avaFormik.values,
        availability: updatedValues,
        availableNonstop: Boolean(availableNonstop),
      });
    }
  }, [availabilities, daysOfWeek, availableNonstop]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (avaFormik.values.availableNonstop !== Boolean(availableNonstop)) {
      avaFormik.setFieldValue(
        "availableNonstop",
        Boolean(availableNonstop),
        false,
      );
    }
  }, [availableNonstop]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (availabilities.length !== 0) {
      const timer = setTimeout(() => {
        setDataLoading(false);
      }, 3000);

      // Return a cleanup function that clears the timeout
      return () => {
        clearTimeout(timer);
      };
    }
  }, [availabilities]);

  function createTimeFromString(timeString) {
    if (!timeString) return null;

    const [hours, minutes] = timeString.split(":").map(Number);
    // Create a Day.js object from the current date, then set the hours and minutes
    return dayjs().hour(hours).minute(minutes);
  }

  function isValidTime(time) {
    if (!time || isNaN(time.getTime())) return false;
    const minutes = time.getMinutes();
    return minutes === 0 || minutes === 30;
  }

  function formatTime(time) {
    const date = new Date(time);
    let hours = date.getHours();
    let minutes = date.getMinutes();

    // Format hours and minutes to ensure two digits
    hours = hours < 10 ? "0" + hours : hours;
    minutes = minutes < 10 ? "0" + minutes : minutes;

    return `${hours}:${minutes}`;
  }

  const handlePrefillTimes = () => {
    setPrefillLoading(true);
    const updatedAvailability = avaFormik.values.availability.map((day) => {
      if (selectedDays.includes(day.day)) {
        return {
          ...day,
          available: true,
          startTime: selectedStartTime, // Ensure these are in the correct format
          endTime: selectedEndTime,
        };
      } else {
        // For days not included in selectedDays, set available to false and clear times
        return {
          ...day,
          available: false,
          startTime: null,
          endTime: null,
        };
      }
    });

    avaFormik.setFieldValue("availability", updatedAvailability);
    // Reset the selected days and times after updating
    setSelectedDays([]);
    setSelectedStartTime(null);
    setSelectedEndTime(null);
    setPrefillLoading(false);
    setReadyChange(false);
  };

  const handleAvailableNonstopChange = async (checked) => {
    setAvailableNonstop(checked);
    await avaFormik.setFieldValue("availableNonstop", checked, false);
    if (checked) {
      const clearedAvailability = avaFormik.values.availability.map((day) => ({
        ...day,
        available: false,
        startTime: null,
        endTime: null,
      }));
      await avaFormik.setFieldValue("availability", clearedAvailability, false);
      avaFormik.setErrors({});
      avaFormik.setTouched({});
      avaFormik.validateForm({
        ...avaFormik.values,
        availableNonstop: true,
        availability: clearedAvailability,
      });
    }
  };

  const handleNotificationToggle = async (event) => {
    const enabled = event.target.checked;
    setNotificationLoading(true);
    setNotificationError("");
    setNotificationSuccess("");

    try {
      if (enabled) {
        // Check if push notifications are supported
        if (!("Notification" in window) || !("serviceWorker" in navigator)) {
          throw new Error(
            "Push notifications are not supported in this browser",
          );
        }

        // Enable notifications
        await enableNotifications(currentSession);
        setPushNotificationsEnabled(true);
        setNotificationPermission("granted");
        setNotificationSuccess("Push notifications enabled successfully!");
      } else {
        // Disable notifications
        await disableNotifications(currentSession);
        setPushNotificationsEnabled(false);
        setNotificationSuccess("Push notifications disabled successfully!");
      }
    } catch (error) {
      console.error("Notification toggle error:", error);

      // Handle specific error cases
      let errorMessage = "Failed to update notification settings";

      if (error.message.includes("Push not supported")) {
        errorMessage = "Push notifications are not supported in this browser";
      } else if (error.message.includes("permission denied")) {
        errorMessage =
          "Notification permission was denied. Please allow notifications in your browser settings.";
        setNotificationPermission("denied");
      } else if (
        error.message.includes("Unexpected token") ||
        error.message.includes("<!doctype")
      ) {
        errorMessage = "Server configuration error. Please contact support.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Specific handling for invalid VAPID/applicationServerKey errors
      if (
        error.name === "InvalidAccessError" ||
        /applicationServerKey|VAPID public key/i.test(error.message || "")
      ) {
        errorMessage =
          "Push notifications misconfigured: invalid VAPID public key. Please contact support.";
      }

      setNotificationError(errorMessage);
      // Revert the toggle state on error
      setPushNotificationsEnabled(!enabled);
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleBlueNotaryLogin = async () => {
    const newTab = openBlankTab();
    try {
      setErrorTextBlueNotary(""); // Clear any previous errors
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/generate-login-link`,
        {
          email: currentUser?.email,
          currentSession: currentSession,
        },
        { withCredentials: true },
      );

      if (resp.data.success) {
        setNewTabLocation(newTab, resp.data.login_link);
      } else {
        newTab?.close();
        setErrorTextBlueNotary(
          resp.data.message || "Failed to generate login link",
        );
      }
    } catch (error) {
      newTab?.close();
      console.error("Error opening BlueNotary dashboard:", error);
      setErrorTextBlueNotary(
        error.response?.data?.message ||
          "Failed to generate login link. Please try again.",
      );
    }
  };

  return (
    <>
      {dataLoading ? (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100vh",
              justifyContent: "center",
              alignItems: "center", // Add this line to center vertically
              flexDirection: "column",
            }}
          >
            <CircularProgress
              color="warning"
              size={isMobile ? "6rem" : "10rem"}
            />
          </Box>
        </>
      ) : (
        <>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              py: isMobile ? 1 : 2,
              backgroundColor: "#EEF2F6",
              minHeight: "100vh",
              width: "100%",
              maxWidth: "100vw",
              overflow: "hidden",
            }}
          >
            <Container
              spacing={isMobile ? 2 : 3}
              maxWidth="lg"
              sx={{
                py: isMobile ? 2 : 3,
                px: isMobile ? 1 : 3,
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              {/* PUSH NOTIFICATION SETTINGS */}
              <Card
                py={isMobile ? 2 : 3}
                px={isMobile ? 2 : 3}
                sx={{
                  width: "100%",
                  maxWidth: isMobile ? "100%" : isTablet ? "85vw" : "60vw",
                  ml: "auto",
                  mr: "auto",
                  mb: 4,
                  boxSizing: "border-box",
                }}
              >
                <CardHeader title="Push Notification Settings" />
                <CardContent>
                  <Stack spacing={isMobile ? 2 : 3}>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Enable push notifications to receive real-time alerts
                        about new notarization requests, appointment updates,
                        and important system notifications.
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          p: isMobile ? 1.5 : 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          backgroundColor: "#fafafa",
                          flexDirection: isMobile ? "column" : "row",
                          gap: isMobile ? 2 : 0,
                        }}
                      >
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            Browser Push Notifications
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {notificationPermission === "denied"
                              ? "Notifications are blocked by browser"
                              : pushNotificationsEnabled
                                ? "Notifications are enabled"
                                : notificationPermission === "granted"
                                  ? "Permission granted. Click to enable notifications"
                                  : "Click to enable notifications"}
                          </Typography>
                        </Box>

                        <FormControlLabel
                          control={
                            <Switch
                              checked={pushNotificationsEnabled}
                              onChange={handleNotificationToggle}
                              disabled={
                                notificationLoading ||
                                notificationPermission === "denied"
                              }
                              color="primary"
                            />
                          }
                          label=""
                        />
                      </Box>

                      {notificationPermission === "denied" && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          Notifications are blocked by your browser. To enable
                          them, please:
                          <br />
                          1. Click the lock icon in your browser's address bar
                          <br />
                          2. Set "Notifications" to "Allow"
                          <br />
                          3. Refresh this page
                        </Alert>
                      )}

                      {notificationError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {notificationError}
                        </Alert>
                      )}

                      {notificationSuccess && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                          {notificationSuccess}
                        </Alert>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* BLUE NOTARY PARTNER DASHBOARD */}
              {currentUser?.notary_admin_id !== "permitRockstar" && (
                <Card
                  py={isMobile ? 2 : 3}
                  px={isMobile ? 2 : 3}
                  sx={{
                    width: "100%",
                    maxWidth: isMobile ? "100%" : isTablet ? "85vw" : "60vw",
                    ml: "auto",
                    mr: "auto",
                    mb: 4,
                    boxSizing: "border-box",
                  }}
                >
                  <CardHeader title="Log into partner's notary dashboard" />
                  <CardContent>
                    <Stack spacing={isMobile ? 2 : 3}>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          This logs you into our partner provider, BlueNotary's
                          notary dashboard where you can add your notary stamp
                          and manage your notary profile.
                        </Typography>
                        {errorTextBlueNotary && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            {errorTextBlueNotary}
                          </Alert>
                        )}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleBlueNotaryLogin}
                          fullWidth
                          size="large"
                          sx={{ mt: 2 }}
                        >
                          Log In
                        </Button>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* Password CHANGE */}
              <Card
                py={isMobile ? 2 : 3}
                px={isMobile ? 2 : 3}
                sx={{
                  width: "100%",
                  maxWidth: isMobile ? "100%" : isTablet ? "85vw" : "60vw",
                  ml: "auto",
                  mr: "auto",
                  mb: 4,
                  boxSizing: "border-box",
                }}
              >
                <CardHeader title="Timezone Settings" />
                <CardContent>
                  <Stack spacing={isMobile ? 2 : 3}>
                    <Typography variant="body2">
                      Select your timezone for notary appointment and dashboard
                      date/time display.
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <TimezoneSelect
                        options={timezoneMappings}
                        timezone={timezone}
                        setTimezone={setTimezone}
                        availabilities={currentUser?.availabilities}
                      />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* Password CHANGE */}
              <Card
                sx={{
                  width: "100%",
                  maxWidth: isMobile ? "100%" : isTablet ? "85vw" : "60vw",
                  ml: "auto",
                  mr: "auto",
                  mb: 4,
                  px: isMobile ? 1 : 3,
                  py: isMobile ? 2 : 3,
                  boxSizing: "border-box",
                }}
              >
                <Grid container justifyContent="center" sx={{ width: "100%" }}>
                  <Grid size={{ sm: 12 }} sx={{ width: "100%" }}>
                    <Typography
                      sx={{
                        textAlign: "center",

                        fontSize: isMobile ? "1.5rem" : "1.75rem",
                        wordBreak: "break-word",
                      }}
                      variant="h5"
                    >
                      Change Password
                    </Typography>
                    <Typography
                      sx={{
                        textAlign: "center",

                        fontSize: isMobile ? "0.875rem" : "1rem",
                        color: "text.secondary",
                        fontStyle: "italic",
                      }}
                    >
                      Note: Changing your password here is separate from your
                      Permit Rockstar contractor login credentials
                    </Typography>
                  </Grid>
                  <Grid size={{ sm: 12 }} sx={{ width: "100%" }}>
                    <form noValidate onSubmit={passwordFormik.handleSubmit}>
                      <Stack
                        spacing={isMobile ? 2 : 3}
                        mt={isMobile ? 2 : 4}
                        sx={{ width: "100%" }}
                      >
                        <TextField
                          error={
                            !!(
                              passwordFormik.touched.oldPassword &&
                              passwordFormik.errors.oldPassword
                            )
                          }
                          helperText={
                            passwordFormik.touched.oldPassword &&
                            passwordFormik.errors.oldPassword
                          }
                          onBlur={passwordFormik.handleBlur}
                          onChange={passwordFormik.handleChange}
                          fullWidth
                          label="Old Password*"
                          name="oldPassword"
                          type={showOldPassword ? "text" : "password"}
                          value={passwordFormik.values.oldPassword}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => {
                                      setShowOldPassword(!showOldPassword);
                                    }}
                                    onMouseDown={(e) => e.preventDefault()} // Prevent focus change on mouse down
                                    onMouseUp={(e) => e.preventDefault()} // Prevent focus change on mouse up
                                    edge="end"
                                  >
                                    {showOldPassword ? (
                                      <Visibility />
                                    ) : (
                                      <VisibilityOff />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                        <TextField
                          error={
                            !!(
                              passwordFormik.touched.newPassword &&
                              passwordFormik.errors.newPassword
                            )
                          }
                          helperText={
                            passwordFormik.touched.newPassword &&
                            passwordFormik.errors.newPassword
                          }
                          onBlur={passwordFormik.handleBlur}
                          onChange={passwordFormik.handleChange}
                          fullWidth
                          label="New Password*"
                          name="newPassword"
                          type={showNewPassword ? "text" : "password"}
                          value={passwordFormik.values.newPassword}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => {
                                      setShowNewPassword(!showNewPassword);
                                    }}
                                    onMouseDown={(e) => e.preventDefault()} // Prevent focus change on mouse down
                                    onMouseUp={(e) => e.preventDefault()} // Prevent focus change on mouse up
                                    edge="end"
                                  >
                                    {showNewPassword ? (
                                      <Visibility />
                                    ) : (
                                      <VisibilityOff />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                        <TextField
                          error={
                            !!(
                              passwordFormik.touched.newPasswordConfirmation &&
                              passwordFormik.errors.newPasswordConfirmation
                            )
                          }
                          helperText={
                            passwordFormik.touched.newPasswordConfirmation &&
                            passwordFormik.errors.newPasswordConfirmation
                          }
                          onBlur={passwordFormik.handleBlur}
                          onChange={passwordFormik.handleChange}
                          fullWidth
                          label="Confirm Password*"
                          name="newPasswordConfirmation"
                          type="password"
                          value={passwordFormik.values.newPasswordConfirmation}
                        />
                        {passwordFormik.errors.submit && (
                          <Typography
                            color="error"
                            sx={{ mt: 3 }}
                            variant="body2"
                          >
                            {passwordFormik.errors.submit}
                          </Typography>
                        )}
                        <Typography
                          variant="body1"
                          color="error"
                          fontWeight="bold"
                          textAlign="center"
                        >
                          {errorTextPassword}
                        </Typography>

                        <LoadingButton
                          loading={loading}
                          type="submit"
                          fullWidth
                          size="large"
                          sx={{
                            mt: 2,
                            backgroundColor: "primary",
                          }}
                          variant="contained"
                        >
                          Submit Changes
                        </LoadingButton>
                      </Stack>
                    </form>
                  </Grid>
                </Grid>
              </Card>

              {/* NOTARY AVAILABILITY */}
              <Box>
                <Card
                  sx={{
                    width: "100%",
                    maxWidth: isMobile ? "100%" : isTablet ? "85vw" : "60vw",
                    ml: "auto",
                    mr: "auto",
                    py: isMobile ? 1 : 3,
                    px: isMobile ? 1 : 3,
                    mb: 4,
                    boxSizing: "border-box",
                  }}
                >
                  <CardHeader title="Change Remote Online Notary (RON) Session Availability" />
                  <CardContent sx={{ px: isMobile ? 1 : 3 }}>
                    <ChangeNotaryAvailability
                      formik={avaFormik}
                      availabilities={availabilities}
                      availableNonstop={availableNonstop}
                      setAvailableNonstop={handleAvailableNonstopChange}
                      saveLoading={saveLoading}
                      settings={true}
                      readyChange={readyChange}
                      setReadyChange={setReadyChange}
                      changeErrorText={changeErrorText}
                      selectedDays={selectedDays}
                      setSelectedDays={setSelectedDays}
                      selectedStartTime={selectedStartTime}
                      setSelectedStartTime={setSelectedStartTime}
                      selectedEndTime={selectedEndTime}
                      setSelectedEndTime={setSelectedEndTime}
                      prefillLoading={prefillLoading}
                      handlePrefillTimes={handlePrefillTimes}
                    />
                  </CardContent>
                </Card>
              </Box>

              {/* CAHGNE 2FA */}
              <ChangeFA
                qrCodeSrc={qrCodeSrc}
                currentUser={currentUser}
                currentFA={currentFA}
                setCurrentFA={setCurrentFA}
                imageList={imageList}
                setImageList={setImageList}
                currentImg={currentImg}
                setCurrentImg={setCurrentImg}
                imgWidth={imgWidth}
                setImgWidth={setImgWidth}
                mobileSize={isMobile}
                tabletSize={isTablet}
                smsConsent={true}
              />
            </Container>
          </Box>
        </>
      )}
    </>
  );
};

export default NotarySettings;
